import * as React from "react";

// NPM
import { animated } from "react-spring";
import moment from "moment";
import clsx from "clsx";

// MUI
import {
	withStyles,
	WithStyles,
	createStyles,
	Theme,
} from "@material-ui/core/styles";
import { Grid, GridProps, Typography } from "@material-ui/core";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { ParsableDate } from "@material-ui/pickers/constants/prop-types";

// Utility
import Utility from "../../domain/classes/Utility";

// Components
import FormattedFieldSet from "../common/FormattedFieldSet.Component";

// Contexts
import { useApplicationManager } from "../../contexts/ApplicationManager.Context";

// Classes
import DateTime from "../../domain/classes/DateTime";
import Patient from "../../domain/classes/Patient";

export const styles = (theme: Theme) =>
	createStyles({
		root: {},
		dobContainer: { display: "flex", flexDirection: "column" },
		dobText: {
			display: "flex",
			alignItems: "center",
			flexGrow: 1,
			justifyContent: "center",
		},
	});

export interface DateOfBirthFieldsContainerV2Props extends GridProps {}

const DateOfBirthFieldsContainerV2 = React.forwardRef<
	HTMLDivElement,
	DateOfBirthFieldsContainerV2Props & WithStyles<typeof styles>
>(({ classes, className, ...gridProps }, ref) => {
	// Contexts
	const { handlePatientDetailsChange } = useApplicationManager();

	// States
	const [dob, setDob] = React.useState<ParsableDate | null>(null);
	const [error, setError] = React.useState<string>("");

	// handlers
	const handleDobChange = (value: ParsableDate) => {
		const isValid =
			moment(value).isValid() && moment(value).isBefore(moment());

		if (isValid) {
			setDob(value);
			setError("");
		} else {
			handlePatientDetailsChange("dateOfBirth", "");
			handlePatientDetailsChange("monthOfBirth", "");
			handlePatientDetailsChange("yearOfBirth", "");
			setError("Invalid date of birth.");
		}
	};

	React.useEffect(() => {
		const dobMoment = moment(dob);

		handlePatientDetailsChange("dateOfBirth", dobMoment.format("DD"));
		handlePatientDetailsChange("monthOfBirth", dobMoment.format("MM"));
		handlePatientDetailsChange("yearOfBirth", dobMoment.format("YYYY"));
	}, [dob, handlePatientDetailsChange]);

	return (
		<Grid
			container
			spacing={1}
			className={clsx(classes.root, className)}
			ref={ref}
			{...gridProps}
		>
			<Grid item xs={12}>
				<FormattedFieldSet label="Date of birth">
					<Grid container spacing={2}>
						<Grid item xs={12} md={6}>
							<KeyboardDatePicker
								inputVariant="outlined"
								value={dob}
								onChange={handleDobChange}
								fullWidth
								format="DD/MM/YYYY"
								size="small"
								label="DD/MM/YYYY"
								error={!!error}
								helperText={error}
							/>
						</Grid>
						<Grid
							item
							xs={12}
							md={6}
							className={classes.dobContainer}
						>
							<Typography
								variant="body2"
								color="primary"
								className={classes.dobText}
							>
								<strong>
									{moment(dob).format(
										DateTime.DATE_DISPLAY_FORMAT
									)}
								</strong>
							</Typography>
							<Typography
								variant="caption"
								className={classes.dobText}
							>
								{Patient.calcAge(
									moment(dob).format(
										DateTime.DEFAULT_DATE_FORMAT
									)
								)}
							</Typography>
						</Grid>
					</Grid>
				</FormattedFieldSet>
			</Grid>
		</Grid>
	);
});

const StyledDateOfBirthFieldsContainerV2 = withStyles(styles, {
	classNamePrefix: Utility.generateClassName("DateOfBirthFieldsContainerV2"),
})(DateOfBirthFieldsContainerV2);

export default StyledDateOfBirthFieldsContainerV2;

export const AnimatedDateOfBirthFieldsContainerV2 = animated(
	StyledDateOfBirthFieldsContainerV2
);
